// ── A material line's price identity must belong to ITS wording ─────────────
// Editor copy of api/_priceIdentity.js (read that file's header for the whole
// story). The quote chat used to reuse a line for a different part and keep
// the OLD part's supplier/sku/product link — "your price (book: TECK 3C8)"
// under a weatherproof in-use box. This strips that foreign identity on load,
// price untouched, so the editor never shows (or re-saves) the wrong part #.
// Keep in step with the api copy — scripts/test_price_identity.js checks both.

const IDENTITY_KEYS = ["supplier_name", "supplier_sku", "supplier_description", "display_description", "master_product_id", "date_recorded", "brand", "req_brand", "book_match", "via_pin", "via_search_fallback"];

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
const alnum = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

function hasIdentity(ps) {
  return !!(ps && typeof ps === "object" && (ps.supplier_sku || ps.supplier_description || ps.master_product_id || ps.book_match));
}

export function wordingNamesPart(desc, ps) {
  const d = alnum(desc);
  if (!d) return false;
  const skus = [ps.supplier_sku, ps.book_match && ps.book_match.supplier_sku].map(alnum).filter((s) => s.length >= 3 && !/^\d+$/.test(s));
  if (skus.some((s) => d.includes(s))) return true;
  const cat = [ps.supplier_description, ps.book_match && ps.book_match.supplier_description].map(alnum).filter((s) => s.length >= 8);
  return cat.some((c) => d.includes(c));
}

export function staleIdentity(m) {
  const ps = m && m._price_source;
  if (!hasIdentity(ps)) return false;
  if (ps.tier === "supplier_quote") return false;
  if (ps.matched_for != null) return norm(ps.matched_for) !== norm(m.description) && !wordingNamesPart(m.description, ps);
  const from = m._patched && m._patched.from && m._patched.from.description;
  if (ps.tier !== "tech_set" || from == null || norm(from) === norm(m.description)) return false;
  return !wordingNamesPart(m.description, ps);
}

export function cleanIdentity(m) {
  if (!staleIdentity(m)) return m;
  const ps = { ...m._price_source };
  for (const k of IDENTITY_KEYS) delete ps[k];
  const from = (m._patched && m._patched.from && m._patched.from.description) || m._price_source.matched_for || null;
  delete ps.matched_for;
  if (from) ps.renamed_from = String(from).slice(0, 160);
  return { ...m, _price_source: ps };
}

export function cleanQuoteIdentities(qj) {
  if (!qj || typeof qj !== "object") return qj;
  let changed = false;
  const fixList = (list) => {
    if (!Array.isArray(list)) return list;
    let hit = false;
    const out = list.map((m) => { const c = cleanIdentity(m); if (c !== m) hit = true; return c; });
    if (hit) changed = true;
    return hit ? out : list;
  };
  const fixDraft = (d) => {
    if (!d || typeof d !== "object") return d;
    const mats = fixList(d.materials);
    return mats === d.materials ? d : { ...d, materials: mats };
  };
  const top = fixDraft(qj);
  const options = Array.isArray(qj.options) ? qj.options.map((o) => { const d = fixDraft(o && o.draft); return d === (o && o.draft) ? o : { ...o, draft: d }; }) : qj.options;
  const sections = Array.isArray(qj.sections) ? qj.sections.map((s) => { const d = fixDraft(s && s.draft); return d === (s && s.draft) ? s : { ...s, draft: d }; }) : qj.sections;
  if (!changed) return qj;
  return { ...top, ...(qj.options ? { options } : {}), ...(qj.sections ? { sections } : {}) };
}
